// Cola de escaneos de Planta y del Baúl. El lector de mano escribe el código
// y manda Enter, y el bulto siguiente puede llegar antes de que conteste el
// servidor. Antes ese segundo Enter se ignoraba y, como el campo se vaciaba
// recién con la respuesta, el código nuevo se pegaba al anterior (o al que
// había fallado). Ahora cada Enter toma el texto, vacía el campo y lo encola;
// los pedidos salen de a uno y en orden, y cada respuesta (o error) queda en
// la lista de la sesión con su código.
//
// Un 401 corta la cola: lo que faltaba mandar queda listado como no enviado.
//
// Con `persistKey`, lo que todavía no tuvo respuesta se anota también en
// sessionStorage: ante un 401, authFetch recarga la página hacia el login
// antes de que se vea la lista, y al salir de la pantalla lo que quedaba en la
// cola no se manda. Al volver a la pantalla (misma pestaña) esos códigos
// aparecen en la lista como «Sin confirmar: volvé a escanearlo».
import { useCallback, useEffect, useRef, useState } from "react";

export type ScanRow<R> = { key: number; code: string; at: Date; result: R | null; error: string | null };
export type ScanQueue<T, R> = {
  value: string;
  setValue: (v: string) => void;
  enter: (toItem: (code: string) => T) => void;
  push: (code: string, item: T) => void;
  rows: ScanRow<R>[];
  pending: number;
  busy: boolean;
  isBusy: () => boolean;
};

type Options<R> = {
  // Cada respuesta, en el orden en que se escaneó.
  onResult?: (result: R, code: string) => void;
  onError?: (message: string, code: string) => void;
  // Una sola vez por tanda, cuando la cola queda vacía (no después de un 401).
  onIdle?: () => void;
  // Clave de sessionStorage para lo que no tuvo respuesta (una por pantalla).
  persistKey?: string;
};

const MAX_ROWS = 200;
export const UNCONFIRMED = "Sin confirmar: volvé a escanearlo";

// sessionStorage puede no estar o tirar (modo privado, cuota): sin él la cola
// anda igual, solo que no recuerda nada.
const readSaved = (key: string): string[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === "string") : [];
  } catch {
    return [];
  }
};
const writeSaved = (key: string, codes: string[]) => {
  try {
    if (codes.length) sessionStorage.setItem(key, JSON.stringify(codes));
    else sessionStorage.removeItem(key);
  } catch {
    // sin sessionStorage no se recuerda
  }
};
// Se anota al encolar y se borra con la respuesta (una vez por código).
const remember = (key: string | undefined, code: string) => {
  if (key) writeSaved(key, [...readSaved(key), code]);
};
const forget = (key: string | undefined, code: string) => {
  if (!key) return;
  const saved = readSaved(key);
  const i = saved.indexOf(code);
  if (i >= 0) writeSaved(key, [...saved.slice(0, i), ...saved.slice(i + 1)]);
};

export function useScanQueue<T, R>(send: (item: T) => Promise<R>, opts: Options<R> = {}): ScanQueue<T, R> {
  const [value, setValueState] = useState("");
  const [rows, setRows] = useState<ScanRow<R>[]>([]);
  // Lo que todavía no tuvo respuesta, incluido el pedido en vuelo.
  const [pending, setPending] = useState(0);
  // El campo también va en una ref: dos Enter seguidos pueden llegar antes
  // del próximo render, y el segundo tiene que ver el campo ya vacío.
  const valueRef = useRef("");
  const queue = useRef<{ code: string; item: T }[]>([]);
  const running = useRef(false);
  const nextKey = useRef(0);
  const alive = useRef(true);
  const sendRef = useRef(send);
  sendRef.current = send;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // Lo que quedó sin respuesta la vez anterior (un 401, o se salió de la
  // pantalla con la cola llena) se lista una vez y se olvida.
  const persistKey = opts.persistKey;
  useEffect(() => {
    if (!persistKey) return;
    const saved = readSaved(persistKey);
    if (!saved.length) return;
    writeSaved(persistKey, []);
    const at = new Date();
    const restored: ScanRow<R>[] = saved.map((code) => ({ key: ++nextKey.current, code, at, result: null, error: UNCONFIRMED }));
    setRows((prev) => [...restored.reverse(), ...prev].slice(0, MAX_ROWS));
  }, [persistKey]);

  const drain = useCallback(async () => {
    const addRow = (code: string, result: R | null, error: string | null) => {
      const row: ScanRow<R> = { key: ++nextKey.current, code, at: new Date(), result, error };
      setRows((prev) => [row, ...prev].slice(0, MAX_ROWS));
    };
    if (running.current) return;
    running.current = true;
    let cut = false;
    // Al salir de la pantalla no se manda lo que quedaba.
    while (queue.current.length && alive.current) {
      const { code, item } = queue.current[0];
      try {
        const result = await sendRef.current(item);
        forget(optsRef.current.persistKey, code);
        addRow(code, result, null);
        optsRef.current.onResult?.(result, code);
      } catch (e: any) {
        const message = e?.message || "Error";
        cut = e?.status === 401;
        // Con un 401 no se sabe si se registró: queda anotado con el resto.
        if (!cut) forget(optsRef.current.persistKey, code);
        addRow(code, null, message);
        optsRef.current.onError?.(message, code);
      }
      queue.current.shift();
      if (cut) for (const q of queue.current.splice(0)) addRow(q.code, null, "No se envió: la sesión venció");
      setPending(queue.current.length);
    }
    running.current = false;
    if (alive.current && !cut) optsRef.current.onIdle?.();
  }, []);

  const push = useCallback((code: string, item: T) => {
    remember(optsRef.current.persistKey, code);
    queue.current.push({ code, item });
    setPending(queue.current.length);
    drain();
  }, [drain]);

  const setValue = useCallback((v: string) => { valueRef.current = v; setValueState(v); }, []);

  // Enter (o «Recibir»): toma lo escrito, vacía el campo y lo encola.
  const enter = useCallback((toItem: (code: string) => T) => {
    const code = valueRef.current.trim();
    setValue("");
    if (code) push(code, toItem(code));
  }, [push, setValue]);

  // Para los botones que no deben sumar a la cola un pedido repetido: lee las
  // refs, así un doble clic antes del render también lo ve.
  const isBusy = useCallback(() => running.current || queue.current.length > 0, []);

  return { value, setValue, enter, push, rows, pending, busy: pending > 0, isBusy };
}
